import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import clsx from "clsx";
import PropTypes from "prop-types";
import React from "react";
import { useUniqueId } from "../../common/hooks";
import FindBy from "../../FindBy/FindBy";
import PanelSelectionsDock from "../../private/PanelSelectionsDock";
import { Button, IconError, IconInformationalLight, Toolbar } from "../..";
import { EDIT, EDITSAVE, PLURAL, REORDER, SINGULAR } from "../constants";
import ListBuilderPanel from "../ListBuilderPanel/ListBuilderPanel";
import extractTheme from "./extractTheme";
/**
 * The EditPanel is a React component which allows to view the Table row data
 * that allows the user to select and de-select rows for reordering.
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";

function EditPanel(_ref) {
  let {
    children,
    openEditPanel,
    selectedRows,
    minSelection,
    maxSelection,
    minSelected,
    maxSelected,
    elementToFocusOnClose,
    customStrings,
    onEditSelect,
    onClose,
    afterSave,
    onReorderOpen,
    findBy,
    handleValueChange,
    handleClear,
    resetHandler,
    fieldValue,
    fieldRef,
    findByContainerAriaLabel,
    editPanelHeaderDockAriaLabel,
    filterHandler,
    editTableData,
    dataIsFiltered,
    panelWidth,
    disableButtons
  } = _ref;
  const theme = useTheme();
  const {
    cssRoot,
    cssFooterButtons,
    cssCancelButton,
    cssReorderButton,
    cssSaveButton
  } = extractTheme({
    theme,
    findBy
  });
  const uniqueId = useUniqueId();
  const feedbackId = `pnr-edit-panel-feedback-${uniqueId}`;
  const selectedCountId = `pnr-edit-panel-selected-count-${uniqueId}`;
  const feedbackErrorProps = {
    icon: _jsx(IconError, {
      size: "small"
    })
  };
  const feedbackDefaultProps = {
    icon: _jsx(IconInformationalLight, {
      size: "small"
    })
  };
  /** Executed whenever `Reorder` button is clicked. */

  const handleReorder = () => {
    onReorderOpen();
  };
  /** Executed whenever `Close` or `Cancel` buttons are clicked. */


  const handleClose = () => {
    onClose(EDIT);
  };
  /** Executed whenever `Save` button is clicked. */


  const handleSave = () => {
    afterSave(EDIT);
  };
  /**
   * @param {array} selectedRows - holds array of selected row `rowKey` values.
   * Executes when selections are made.
   */


  const handleEditSelect = _ref2 => {
    let {
      selectedRows
    } = _ref2;
    onEditSelect({
      selectedRows
    });
  };
  /**
   * @description This method wraps the `FindBy` element
   * in the `Toolbar` only if the findBy prop is set to true.
   * It also configures the handlers that needs to be
   * triggered on click of the button controls of `FindBy` component.
   * @return {element} `FindBy` wrapped in a `Toolbar`.
   */


  const renderFindBy = () => {
    const {
      findButtonAriaLabel,
      resetButtonAriaLabel,
      resetAnnouncement,
      findAnnouncement
    } = customStrings;
    return findBy ? _jsx(Toolbar, {
      theme: "chromeless",
      "aria-label": customStrings.toolBarAriaLabel,
      className: "pnr-list-builder-panel__toolbar",
      noVerticalPadding: true,
      children: _jsx(FindBy, {
        value: fieldValue,
        onValueChange: handleValueChange,
        onFind: filterHandler,
        onReset: resetHandler,
        onClear: handleClear,
        ref: fieldRef,
        name: "listbuilder-findby",
        "aria-label": findByContainerAriaLabel,
        findAnnouncement: dataIsFiltered ? findAnnouncement(editTableData.length) : "",
        customStrings: {
          findButtonAriaLabel,
          resetButtonAriaLabel,
          resetAnnouncement
        }
      })
    }) : null;
  };

  const {
    closeButtonAriaLabelEditPanel,
    editPanelTitle,
    saveButtonLabel,
    reorderButtonLabel,
    cancelButtonLabel,
    feedbackInstruction,
    feedbackMaxSelectionError,
    feedbackMinSelectionError,
    editPanelSelectedRowsMessage
  } = customStrings;
  const selectedRowsCount = selectedRows.length;
  const selectable = children.props && children.props.selectable;
  const selectAll = selectable && maxSelection < 1 ? selectable.selectAll : false;
  let error = false,
      feedbackMessage = feedbackInstruction === editPanelTitle ? "" : feedbackInstruction,
      feedbackProps = feedbackMessage ? feedbackDefaultProps : {};

  if (maxSelection < selectedRowsCount && maxSelected && maxSelection > 0) {
    feedbackMessage = feedbackMaxSelectionError;
    feedbackProps = feedbackErrorProps;
    error = true;
  } else if (minSelection > selectedRowsCount && minSelected && minSelection > 0) {
    feedbackMessage = feedbackMinSelectionError;
    feedbackProps = feedbackErrorProps;
    error = true;
  }

  const containerFeedback = clsx({
    "pnr-list-builder__feedback--error": error
  });
  const item = selectedRowsCount === 1 ? SINGULAR : PLURAL;
  const selectedItemsCountMessage = editPanelSelectedRowsMessage(selectedRowsCount, item);
  const findByComponent = renderFindBy();
  const {
    ariaCaption,
    caption
  } = children.props;
  return _jsx(ListBuilderPanel, {
    customStrings: {
      closeButtonAriaLabel: closeButtonAriaLabelEditPanel
    },
    open: openEditPanel,
    width: panelWidth,
    title: editPanelTitle,
    elementToFocusOnClose: elementToFocusOnClose,
    className: "pnr-edit-panel",
    css: cssRoot,
    onClose: handleClose,
    "aria-describedby": `${feedbackId} ${selectedCountId}`,
    headerDockAriaLabel: editPanelHeaderDockAriaLabel,
    headerDock: _jsxs(React.Fragment, {
      children: [_jsx(PanelSelectionsDock, {
        className: containerFeedback,
        feedbackIcon: feedbackProps.icon,
        feedbackMessage: feedbackMessage,
        feedbackAriaLive: "polite",
        feedbackError: error,
        selectedCountMessage: selectedItemsCountMessage,
        feedbackId: feedbackId,
        selectedCountId: selectedCountId
      }), findByComponent]
    }),
    footer: _jsxs("div", {
      css: cssFooterButtons,
      children: [_jsx(Button, {
        kind: "standard",
        label: cancelButtonLabel,
        css: cssCancelButton,
        onClick: handleClose
      }), _jsx(Button, {
        kind: "standard",
        label: reorderButtonLabel,
        css: cssReorderButton,
        disabled: error || !minSelected && minSelection > 0 || disableButtons.includes(REORDER),
        onClick: handleReorder
      }), _jsx(Button, {
        kind: "primary",
        label: saveButtonLabel,
        css: cssSaveButton,
        disabled: error || !minSelected && minSelection > 0 || disableButtons.includes(EDITSAVE),
        onClick: handleSave
      })]
    }),
    children: /*#__PURE__*/React.cloneElement(children, {
      ariaCaption: ariaCaption || caption,
      caption: null,
      data: editTableData,
      nonStickyHeader: true,
      selectable: _objectSpread(_objectSpread({}, children.props.selectable), {}, {
        selectedRows,
        selectAll,
        selectionType: "multi",
        onSelectedChange: handleEditSelect
      })
    })
  });
}

EditPanel.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Callback to be executed after click of `Save` button. */
  afterSave: PropTypes.func.isRequired,

  /** This is what will be displayed inside the Panel. */
  children: PropTypes.node.isRequired,

  /** Override for strings used within the component. */
  customStrings: PropTypes.shape({
    /** aria-label for Panel's close button. */
    closeButtonAriaLabelEditPanel: PropTypes.string,

    /** Title of the Edit panel. */
    editPanelTitle: PropTypes.string,

    /** Save button label that will be displayed in Edit and Reorder panels. */
    saveButtonLabel: PropTypes.string,

    /** Cancel button label that will be displayed in Edit and Reorder panels. */
    cancelButtonLabel: PropTypes.string,

    /** Reorder button label. */
    reorderButtonLabel: PropTypes.string,

    /** General message to be shown in Feedback component. */
    feedbackInstruction: PropTypes.string,

    /** Error message to display when the minimum selections have not been made. */
    feedbackMinSelectionError: PropTypes.string,

    /** Error message to display when the maximum selections have been exceeded. */
    feedbackMaxSelectionError: PropTypes.string,

    /**
     * Render prop for the selected rows count message in the Edit Panel.
     * It will be called with the following arguments:
     *
     * <ul>
     *  <li>
     *    `selectedRowsCount`: Number of rows selected in the Edit Panel.
     *  </li>
     *  <li>
     *    `item`: This holds the singular or plural of item, based on `selectedRowsCount`.
     *  </li>
     * </ul>
     */
    editPanelSelectedRowsMessage: PropTypes.func,

    /** aria-label for the toolbar. */
    toolBarAriaLabel: PropTypes.string,

    /**
     * Text to be announced by the screen readers when "Go" button is triggered.
     * Applicable only when `FindBy` is configured.
     * Defaults to "${filteredRecordCount} items found"
     * It will be called with the following argument:
     * <ul>
     *  <li>
     *    `filteredRecordCount`: Number of rows filtered in the Edit Panel.
     *  </li>
     * </ul>
     */
    findAnnouncement: PropTypes.func,

    /**
     * Text to be announced by the screen readers when "Reset" button is triggered.
     * Applicable only when `FindBy` is configured.
     * Defaults to "Results and search settings have been reset"
     */
    resetAnnouncement: PropTypes.string,

    /**
     * aria-label which will be applied to "Go" button.
     * Defaults to "Go search data".
     */
    findButtonAriaLabel: PropTypes.string,

    /**
     * aria-label which will be applied to "Reset" button.
     * Defaults to "Reset results and clear".
     */
    resetButtonAriaLabel: PropTypes.string
  }).isRequired,

  /** Whether or not the data set is filtered by a FindBy */
  dataIsFiltered: PropTypes.bool.isRequired,

  /** aria-label for Edit Panel header dock */
  editPanelHeaderDockAriaLabel: PropTypes.string.isRequired,

  /** Data that is displayed in Edit Panel. */
  // eslint-disable-next-line react/forbid-prop-types
  editTableData: PropTypes.arrayOf(PropTypes.object).isRequired,

  /**
   * This prop identifies the element that should receive
   * focus after Edit panel is closed.
   * It can be a css selector or a function which
   * returns a DOM node.
   */
  elementToFocusOnClose: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,

  /** Handler for storing the input field reference. */
  fieldRef: PropTypes.func.isRequired,

  /** Value of the input field. */
  fieldValue: PropTypes.string.isRequired,

  /** Whether to render `FindBy` component or not. */
  findBy: PropTypes.bool.isRequired,

  /** Handler for clearing the field value. */
  handleClear: PropTypes.func.isRequired,

  /** Handler for storing the field value. */
  handleValueChange: PropTypes.func.isRequired,

  /** Indicates if maximum number of selections have been reached at least once. */
  maxSelected: PropTypes.bool.isRequired,

  /** Maximum number of selections that can be made in Edit panel. */
  maxSelection: PropTypes.number.isRequired,

  /** Indicates if minimum number of selections have been reached at least once. */
  minSelected: PropTypes.bool.isRequired,

  /** Minimun number of selections to be made in Edit panel. */
  minSelection: PropTypes.number.isRequired,

  /** Callback handler to be executed after click of `Close` or `Cancel` buttons. */
  onClose: PropTypes.func.isRequired,

  /** Executes whenever a `row` or `selectAll` checkbox has been selected. */
  onEditSelect: PropTypes.func.isRequired,

  /** Handler used to open Reorder panel. Executed when `Reorder` button is clicked. */
  onReorderOpen: PropTypes.func.isRequired,

  /** Indicates if Edit panel is in open/close state. True if open. */
  openEditPanel: PropTypes.bool.isRequired,

  /** Callback to be triggered on click of reset button. */
  resetHandler: PropTypes.func.isRequired,

  /** An array of `id` values that indicate the corresponding data set is selected. */
  selectedRows: PropTypes.array.isRequired,
  // eslint-disable-line react/forbid-prop-types

  /**
   * Configuration to disable footer `Reorder`, `Save` buttons.
   * @ignore
   */
  disableButtons: PropTypes.arrayOf(PropTypes.string),

  /** Handler that gets executed when Filter button is triggered. */
  filterHandler: PropTypes.func,

  /** aria-label which will be applied to the FindBy container. */
  findByContainerAriaLabel: PropTypes.string,

  /** Width of the Edit Panel. */
  panelWidth: PropTypes.string
} : {};
EditPanel.defaultProps = {
  dataIsFiltered: false,
  findByContainerAriaLabel: "",
  filterHandler: null,
  panelWidth: null,
  disableButtons: []
};
EditPanel.displayName = "EditPanel";
export default EditPanel;